import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { financeApi, isDocumentVoucher, type JournalVoucher } from '@/api/financeApi';
import { formatINR } from '@/utils/format';

type StatusFilter = 'ALL' | 'DRAFT' | 'POSTED' | 'CANCELLED' | 'REVERSED';

export default function JournalVoucherListPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [vouchers, setVouchers] = useState<JournalVoucher[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadVouchers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await financeApi.getJournalVouchers({
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        fromDate: dateFrom || undefined,
        toDate: dateTo || undefined,
        search: search.trim() || undefined,
        page,
        limit: 25,
      });
      setVouchers(res.vouchers);
      setTotal(res.total);
    } catch (err: any) {
      console.error('Failed to load journal vouchers:', err);
      toast.error(err.response?.data?.message || 'Failed to load journal vouchers');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo, search, page, toast]);

  useEffect(() => {
    loadVouchers();
  }, [loadVouchers]);

  const summary = useMemo(() => {
    const posted = vouchers.filter((j) => j.status === 'POSTED').length;
    const draft = vouchers.filter((j) => j.status === 'DRAFT').length;
    const reversed = vouchers.filter((j) => j.status === 'REVERSED' || j.isReversed).length;
    const totalDebitPosted = vouchers
      .filter((j) => j.status === 'POSTED')
      .reduce((s, j) => s + Number(j.totalDebit || 0), 0);
    return { posted, draft, reversed, totalDebitPosted };
  }, [vouchers]);

  const handlePost = async (jv: JournalVoucher) => {
    try {
      await financeApi.postJournalVoucher(jv.id);
      toast.success(`${jv.voucherNumber} posted to General Ledger`);
      loadVouchers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to post voucher');
    }
  };

  const handleReverse = async (jv: JournalVoucher) => {
    const reason = prompt(`Enter reason for reversing ${jv.voucherNumber}:`, 'Voucher adjustment / correction');
    if (reason === null) return;
    try {
      const rev = await financeApi.reverseJournalVoucher(jv.id, { reason });
      toast.success(`Voucher reversed successfully. Generated ${rev.voucherNumber}.`);
      loadVouchers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reverse voucher');
    }
  };

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-amber-100 text-amber-800 border-amber-200',
    POSTED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    CANCELLED: 'bg-rose-100 text-rose-800 border-rose-200',
    REVERSED: 'bg-purple-100 text-purple-800 border-purple-200',
  };

  const formatDate = (d: string) => {
    if (!d) return '—';
    const dt = new Date(d.includes('T') ? d : d + 'T00:00:00');
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto pb-16">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-[#1e293b]">Journal Vouchers (JV)</h1>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                  Manual & System General Ledger
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                Record adjustments, provisions, depreciation, salary payments, and audit adjustments.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/finance/accounts')}
                className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
              >
                <i className="ri-node-tree text-sm text-primary-600" /> Chart of Accounts
              </button>
              <button
                onClick={() => navigate('/finance/journal/new')}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <i className="ri-add-line text-sm" /> New Journal Voucher
              </button>
            </div>
          </div>

          {/* Metric Cards */}
          <div className="px-6 pt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Posted</p>
              <p className="text-xl font-bold text-emerald-700 mt-1">{summary.posted}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Active in General Ledger</p>
            </div>
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Draft Vouchers</p>
              <p className="text-xl font-bold text-amber-600 mt-1">{summary.draft}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Pending review & posting</p>
            </div>
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Reversed Vouchers</p>
              <p className="text-xl font-bold text-purple-700 mt-1">{summary.reversed}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Audit-corrected entries</p>
            </div>
            <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Debits Posted</p>
              <p className="text-xl font-bold text-[#1e293b] mt-1">{formatINR(summary.totalDebitPosted)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Balanced with credits</p>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="px-6 pt-5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search voucher no or narration..."
                  className="w-64 h-9 pl-8 pr-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] placeholder:text-slate-400"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] cursor-pointer"
              >
                <option value="ALL">All Statuses</option>
                <option value="DRAFT">Draft</option>
                <option value="POSTED">Posted</option>
                <option value="REVERSED">Reversed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>

              <div className="flex items-center gap-1.5 text-sm text-slate-600 bg-white border border-[#e2e8f0] rounded-lg px-2.5 h-9">
                <span className="text-slate-400">From:</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="text-sm text-[#1e293b] focus:outline-none bg-transparent"
                />
                <span className="text-slate-400">To:</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="text-sm text-[#1e293b] focus:outline-none bg-transparent"
                />
              </div>
            </div>

            <span className="text-xs text-slate-500 font-medium">Total: {total} vouchers</span>
          </div>

          {/* Table */}
          <div className="px-6 pt-5">
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              {loading ? (
                <div className="py-16 text-center text-slate-400 flex flex-col items-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
                  <p className="text-xs">Loading Journal Vouchers...</p>
                </div>
              ) : vouchers.length === 0 ? (
                <div className="py-16 text-center text-slate-400 flex flex-col items-center gap-2">
                  <i className="ri-file-list-3-line text-3xl text-slate-300" />
                  <p className="text-sm font-semibold text-slate-700">No Journal Vouchers Found</p>
                  <p className="text-xs text-slate-400">Create a new manual journal entry or adjust filters.</p>
                  <button
                    onClick={() => navigate('/finance/journal/new')}
                    className="mt-3 px-4 py-1.5 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm"
                  >
                    + New Journal Voucher
                  </button>
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-100 text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                    <tr>
                      <th className="text-left px-5 py-3 w-36">Voucher #</th>
                      <th className="text-left px-4 py-3 w-28">Date</th>
                      <th className="text-left px-4 py-3 w-32">Type</th>
                      <th className="text-left px-4 py-3">Narration</th>
                      <th className="text-right px-4 py-3 w-36">Total Amount</th>
                      <th className="text-center px-4 py-3 w-28">Status</th>
                      <th className="text-center px-4 py-3 w-36">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {vouchers.map((jv) => (
                      <tr
                        key={jv.id}
                        onClick={() => navigate(`/finance/journal/${jv.id}`)}
                        className="hover:bg-primary-50/30 cursor-pointer transition-colors"
                      >
                        <td className="px-5 py-3 font-mono font-bold text-primary-700 flex items-center gap-1.5">
                          {jv.voucherNumber}
                          {jv.isReversed && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-sans font-semibold">
                              Reversed
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{formatDate(jv.voucherDate)}</td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-200/70 text-slate-700">
                            {jv.voucherType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-800 max-w-xs truncate" title={jv.narration}>
                          {jv.narration || <span className="text-slate-300 italic">No narration</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold text-[#1e293b]">
                          {formatINR(jv.totalDebit)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${
                              statusColors[jv.status] || 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {jv.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => navigate(`/finance/journal/${jv.id}`)}
                              className="px-2 py-1 rounded text-slate-600 hover:bg-slate-200/70 text-[11px] font-medium transition-colors"
                              title="View voucher details"
                            >
                              View
                            </button>
                            {jv.status === 'DRAFT' && (
                              <button
                                onClick={() => handlePost(jv)}
                                className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-[11px] font-semibold transition-colors border border-emerald-200"
                                title="Post to General Ledger"
                              >
                                Post
                              </button>
                            )}
                            {jv.status === 'POSTED' && !jv.isReversed && !isDocumentVoucher(jv) && (
                              <button
                                onClick={() => handleReverse(jv)}
                                className="px-2 py-1 rounded bg-purple-50 text-purple-700 hover:bg-purple-100 text-[11px] font-semibold transition-colors border border-purple-200"
                                title="Reverse this voucher"
                              >
                                Reverse
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}